import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { AuthService } from '../../core/services/auth';
import { UserService, ChangePasswordPayload } from '../../core/services/user';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './change-password.html',
  styleUrl: './change-password.css',
})
export class ChangePasswordComponent {
  private router = inject(Router);
  private auth = inject(AuthService);
  private userService = inject(UserService);
  private fb = inject(FormBuilder);

  form = this.fb.group({
    current_password: ['', Validators.required],
    new_password: ['', [Validators.required, Validators.minLength(6)]],
    confirm_password: ['', Validators.required],
  });

  errorMessage = signal('');
  successMessage = signal('');
  isLoading = signal(false);

  get currentUser() {
    return this.auth.currentUser();
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage.set('Please fill all fields. New password must be at least 6 characters.');
      return;
    }

    const { current_password, new_password, confirm_password } = this.form.getRawValue();
    if (new_password !== confirm_password) {
      this.errorMessage.set('New password and confirmation do not match.');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    const payload: ChangePasswordPayload = {
      current_password: current_password!,
      new_password: new_password!,
    };

    this.userService.changePassword(payload).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.successMessage.set('Password updated. Redirecting…');
        this.form.reset();
        this.auth.fetchCurrentUser().subscribe({
          next: () => this.router.navigate(['/dashboard']),
          error: () => this.router.navigate(['/dashboard']),
        });
      },
      error: (err) => {
        this.isLoading.set(false);
        const detail = err?.error?.detail ?? err?.error?.message;
        this.errorMessage.set(typeof detail === 'string' ? detail : 'Failed to change password. Please try again.');
      },
    });
  }
}
